/**
 * Auto-mapping helpers for the per-list "Bulk actions" dropdown importer
 * used on the Leads, Clients, and Invoices pages
 * (`client/src/components/bulk-actions-dropdown.tsx`).
 *
 * This is the second of two CSV importers in the app — the other is the
 * Bulk Import (`/import`) flow whose helpers live in
 * `./csv-import-mapping.ts`. The two importers were written separately and
 * use slightly different normalisation rules and target-field vocabularies
 * (e.g. the dropdown maps `Account Name` → `leadName` for the Leads flow,
 * while the Bulk Import flow has no such alias). They share the same
 * dual-header guarantee though: legacy export headers
 * (`Show Date`, `Date of Show`, `Show Rate`, `Rate`, `Amount`) and the
 * current export headers (`Date of Event`, `Event Rate`) must both
 * auto-map to the same internal target field so older customer
 * spreadsheets keep importing.
 *
 * The base `HEADER_ALIASES` table below is leads-flavoured (e.g.
 * `Account Name` → `leadName`). Clients and Invoices use different
 * target field keys for the same column header — `accountName` instead
 * of `leadName`, and `amount` instead of `eventRate` for invoices —
 * so callers pass an `entityType` and we layer entity-specific overrides
 * on top of the base table. See `ENTITY_OVERRIDES` below.
 *
 * If you remove or rename any legacy/current alias here, the dual-header
 * test in `bulk-actions-csv-mapping.test.ts` will fail — please update
 * the test intentionally rather than silently dropping support.
 */

export interface BulkActionsFieldConfig {
  key: string;
  label: string;
  required?: boolean;
}

export type BulkActionsEntity = "leads" | "clients" | "invoices";

/**
 * Normalise a CSV header for alias lookup: strip BOM, lower-case, and
 * remove whitespace, underscores, and parentheses. We deliberately keep
 * other punctuation so `email-address` stays distinct from `emailaddress`.
 */
const normalizeHeader = (header: string): string =>
  header.replace(/^\uFEFF/, "").toLowerCase().replace(/[_\s\(\)]/g, "");

/**
 * Header → target-field-key alias table. Keys are normalised via
 * `normalizeHeader` above.
 *
 * This is the leads-flavoured base table — Clients and Invoices override
 * a few entries via `ENTITY_OVERRIDES` below. Both legacy and current
 * export labels are listed for any header that has changed names over
 * time, so that re-importing an older customer spreadsheet through the
 * dropdown importer keeps producing the same mapping as a freshly
 * exported one.
 */
const HEADER_ALIASES: Record<string, string> = {
  // Leads
  region: "regionName",
  regionname: "regionName",
  territory: "regionName",
  accountname: "leadName",
  company: "leadName",
  facilityname: "leadName",
  venuename: "leadName",
  venue: "leadName",
  artist: "artist",
  emailoptedout: "emailOptedOut",
  optedout: "emailOptedOut",
  unsubscribed: "emailOptedOut",
  // Address fields
  address: "address",
  streetaddress: "address",
  street: "address",
  city: "city",
  state: "state",
  stateprovince: "state",
  zip: "zipCode",
  zipcode: "zipCode",
  postalcode: "zipCode",
  // Rating
  rating: "rating",
  stars: "rating",
  starrating: "rating",
  // Website
  website: "website",
  url: "website",
  weburl: "website",
  websiteurl: "website",
  homepage: "website",
  site: "website",
  // Venue type (specific) — separate from client type (parent)
  venuetype: "venueType",
  type: "venueType",
  category: "venueType",
  subtype: "venueType",
  placetype: "venueType",
  // Other venue details
  capacity: "venueCapacity",
  venuecapacity: "venueCapacity",
  // Client type (parent category)
  clienttype: "clientType",
  parenttype: "clientType",
  segment: "clientType",
  // Clients
  clientname: "accountName",
  phone: "phoneNumber",
  email: "emailAddress",
  contact: "contactName",
  // Date of event — legacy export labels (`Show Date`, `Date of Show`,
  // `Date`) AND the current label (`Date of Event`, `Event Date`). The
  // current labels are listed here too so they auto-map even when the
  // caller's field config doesn't happen to expose `Date of Event` as a
  // matching label/key (e.g. the leads field config doesn't).
  date: "dateOfEvent",
  showdate: "dateOfEvent",
  dateofshow: "dateOfEvent",
  dateofevent: "dateOfEvent",
  eventdate: "dateOfEvent",
  // Event rate — legacy export labels (`Rate`, `Amount`, `Show Rate`)
  // AND the current label (`Event Rate`). See the date comment above for
  // why the current label is duplicated here.
  amount: "eventRate",
  rate: "eventRate",
  showrate: "eventRate",
  eventrate: "eventRate",
};

/**
 * Per-entity overrides on top of `HEADER_ALIASES`. The base table is
 * leads-flavoured (e.g. `Account Name` → `leadName`); Clients and
 * Invoices use different target field keys for some of the same headers.
 *
 * Each override map is keyed by a normalised header (same normalisation
 * as `HEADER_ALIASES`) and short-circuits the lookup before the base
 * alias table is consulted.
 *
 *   - Clients: `Account Name` → `accountName` (the actual field key in
 *     `CLIENT_EXPORT_FIELDS`), instead of the leads-flavoured `leadName`.
 *     Money-column headers (`Amount` / `Rate` / `Show Rate` /
 *     `Event Rate`) are also rerouted to `rate` — the schema field
 *     for the booking price on the `clients` table — instead of the
 *     leads-flavoured `eventRate` default. Without this override,
 *     re-importing a freshly exported clients CSV would auto-map the
 *     money column to `eventRate`, which is not in
 *     `CLIENT_EXPORT_FIELDS` nor in the bulk-update whitelist on
 *     `PATCH /api/bulk/clients`, so the value would be silently
 *     dropped and the user would have to fix the mapping every time.
 *   - Invoices: `Account Name` → `accountName`, AND
 *     `Amount` / `Rate` / `Show Rate` / `Event Rate` → `amount` (the
 *     field key in `INVOICE_EXPORT_FIELDS`), instead of `eventRate`.
 *     Same rationale as the Clients money-column override above.
 */
const ENTITY_OVERRIDES: Record<BulkActionsEntity, Record<string, string>> = {
  leads: {},
  clients: {
    accountname: "accountName",
    company: "accountName",
    facilityname: "accountName",
    venuename: "accountName",
    venue: "accountName",
    amount: "rate",
    rate: "rate",
    showrate: "rate",
    eventrate: "rate",
  },
  invoices: {
    accountname: "accountName",
    company: "accountName",
    facilityname: "accountName",
    venuename: "accountName",
    venue: "accountName",
    amount: "amount",
    rate: "amount",
    showrate: "amount",
    eventrate: "amount",
  },
};

/**
 * CSV column header strings that should always map to the entity's
 * primary-key field (used by the dropdown importer's "Update" mode).
 */
const ID_HEADERS = new Set([
  "recordid",
  "id",
  "clientid",
  "leadid",
  "invoiceid",
]);

/**
 * Auto-map a list of CSV headers from the dropdown importer's CSV upload
 * to internal field keys. Returns a record keyed by the column INDEX as a
 * string (matching the existing component's `fieldMapping` shape — the
 * UI looks up values by `row[parseInt(csvIndex)]`).
 *
 * Resolution order:
 *   1. Special-case ID headers (always map to `"id"`).
 *   2. Lookup in the entity-specific override map (if `entityType` is
 *      passed) — this is how Clients/Invoices reroute `Account Name` and
 *      `Amount` away from the leads-flavoured defaults.
 *   3. Lookup in `HEADER_ALIASES` (leads-flavoured base table).
 *   4. Match against the caller-provided field config (label or key,
 *      normalised the same way).
 *
 * Headers that don't resolve are simply omitted; the dropdown UI lets the
 * user map them manually.
 *
 * `entityType` is optional for backward-compatibility with older
 * callers/tests that don't care about per-entity disambiguation; when
 * omitted, the function falls straight through to `HEADER_ALIASES`.
 */
export function autoMapBulkActionsHeaders(
  headers: readonly string[],
  fields: readonly BulkActionsFieldConfig[],
  entityType?: BulkActionsEntity,
): Record<string, string> {
  const mapping: Record<string, string> = {};
  const overrides = entityType ? ENTITY_OVERRIDES[entityType] : undefined;

  headers.forEach((header, index) => {
    const normalized = normalizeHeader(header);

    if (ID_HEADERS.has(normalized)) {
      mapping[String(index)] = "id";
      return;
    }

    if (overrides && overrides[normalized]) {
      mapping[String(index)] = overrides[normalized];
      return;
    }

    if (HEADER_ALIASES[normalized]) {
      mapping[String(index)] = HEADER_ALIASES[normalized];
      return;
    }

    const matchedField = fields.find((f) => {
      const normLabel = f.label.toLowerCase().replace(/[_\s\(\)]/g, "");
      const normKey = f.key.toLowerCase().replace(/[_\s\(\)]/g, "");
      return normLabel === normalized || normKey === normalized;
    });

    if (matchedField) {
      mapping[String(index)] = matchedField.key;
    }
  });

  return mapping;
}
